import React, { useState, useEffect, useCallback } from 'react';
import { extranetAPI, productAPI, emailTemplateAPI } from '../../services/api';
import { Button, Modal, FormField, Input, Textarea } from '../../components/common';
import { toast } from 'react-toastify';

const STATUS = {
  draft:       { label: 'Brouillon',      color: '#64748b', bg: '#f1f5f9' },
  sent:        { label: 'Envoyé',         color: '#2563eb', bg: '#dbeafe' },
  active:      { label: 'En cours',       color: '#7c3aed', bg: '#ede9fe' },
  pending_bat: { label: 'BAT en attente', color: '#d97706', bg: '#fef3c7' },
  bat_signed:  { label: 'BAT signé',      color: '#16a34a', bg: '#dcfce7' },
  closed:      { label: 'Clôturé',        color: '#94a3b8', bg: '#f8fafc' },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.draft;
  return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg }}>{s.label}</span>;
};

// ═════════════════════════════════════════════════════════════
//  PANEL DÉTAIL EXTRANET
// ═════════════════════════════════════════════════════════════
const ExtranetPanel = ({ id, onRefresh }) => {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [email,         setEmail]         = useState('');
  const [sending,       setSending]       = useState(false);
  const [sendingBat,    setSendingBat]    = useState(false);
  // Produits
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const [addForm,       setAddForm]       = useState({ product_id: null, product_name: '', custom_price: '', quantity: 1, notes: '' });
  const [showAddForm,   setShowAddForm]   = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);
  const [removingId,    setRemovingId]    = useState(null);
  // Fichiers
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingFileId,setDeletingFileId]= useState(null);
  // Preuve BAT
  const [batProof,      setBatProof]      = useState(null);
  const [proofModal,    setProofModal]    = useState(false);
  // Actions
  const [steppingBack,  setSteppingBack]  = useState(false);
  const [toggling,      setToggling]      = useState(false);
  // Templates email
  const [templates,     setTemplates]     = useState([]);
  const [tplPropositions, setTplPropositions] = useState('');
  const [tplBat,        setTplBat]        = useState('');

  // ── Chargement données ─────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await extranetAPI.get(id);
      const d = r.data.data;
      setData(d);
      setEmail(d.client_email || '');
    } catch { toast.error('Chargement impossible'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Charger les templates email une seule fois
  useEffect(() => {
    emailTemplateAPI.list()
      .then(({ data }) => setTemplates((data.data || []).filter(t => t.is_active)))
      .catch(() => {});
  }, []);

  // ── Recherche produits catalogue ───────────────────────
  useEffect(() => {
    if (productSearch.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      productAPI.list({ search: productSearch, per_page: 8 })
        .then(({ data: r }) => setSearchResults(r.data || []))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [productSearch]);

  // ── Actions produits ──────────────────────────────────
  const selectProduct = (p) => {
    setAddForm(f => ({ ...f, product_id: p.id, product_name: p.name, custom_price: p.base_price || '' }));
    setProductSearch(p.name);
    setSearchResults([]);
  };

  const addProduct = async () => {
    setAddingProduct(true);
    try {
      await extranetAPI.addProduct(id, {
        product_id: addForm.product_id || null,
        custom_price: addForm.custom_price || null,
        quantity: parseInt(addForm.quantity) || 1,
        notes: addForm.notes || null,
      });
      toast.success('Produit ajouté');
      setAddForm({ product_id: null, product_name: '', custom_price: '', quantity: 1, notes: '' });
      setProductSearch('');
      setShowAddForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setAddingProduct(false); }
  };

  const removeProduct = async (pid) => {
    setRemovingId(pid);
    try { await extranetAPI.removeProduct(id, pid); toast.success('Produit retiré'); load(); }
    catch { toast.error('Erreur'); }
    finally { setRemovingId(null); }
  };

  // ── Actions fichiers ──────────────────────────────────
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 20 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 20 Mo)'); return; }
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'bat');
      await extranetAPI.uploadFile(id, fd);
      toast.success('Fichier uploadé');
      load(); // Recharger tout (show() retourne maintenant les fichiers)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur upload');
    }
    finally { setUploadingFile(false); }
  };

  const deleteFile = async (fid) => {
    setDeletingFileId(fid);
    try { await extranetAPI.deleteFile(id, fid); toast.success('Fichier supprimé'); load(); }
    catch { toast.error('Erreur'); }
    finally { setDeletingFileId(null); }
  };

  // ── Toggle statut draft ↔ sent ────────────────────────
  const toggleStatus = async () => {
    setToggling(true);
    try {
      const r = await extranetAPI.toggleStatus(id);
      toast.success(`Statut changé en « ${STATUS[r.data.data?.status]?.label || r.data.data?.status} »`);
      load(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setToggling(false); }
  };

  // ── Envoyer propositions (étape 1) ────────────────────
  const sendPropositions = async () => {
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    setSending(true);
    try {
      const payload = { client_email: email, base_url: window.location.origin };
      if (tplPropositions) payload.template_id = parseInt(tplPropositions);
      const r = await extranetAPI.sendAccess(id, payload);
      toast.success('Propositions envoyées au client');
      if (r.data?.data?.portal_url) navigator.clipboard.writeText(r.data.data.portal_url).catch(() => {});
      load(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSending(false); }
  };

  // ── Envoyer BAT (étape 2) ────────────────────────────
  const sendBat = async () => {
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    setSendingBat(true);
    try {
      const payload = { client_email: email, base_url: window.location.origin };
      if (tplBat) payload.template_id = parseInt(tplBat);
      await extranetAPI.sendBat(id, payload);
      toast.success('BAT envoyé au client');
      load(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSendingBat(false); }
  };

  // ── Step back ─────────────────────────────────────────
  const doStepBack = async () => {
    if (!window.confirm('Revenir au statut précédent ?')) return;
    setSteppingBack(true);
    try { await extranetAPI.stepBack(id); toast.success('Statut réinitialisé'); load(); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSteppingBack(false); }
  };

  // ── Clôturer ──────────────────────────────────────────
  const close = async () => {
    if (!window.confirm('Clôturer cet extranet ? Le client n\'aura plus accès.')) return;
    try { await extranetAPI.close(id); toast.success('Clôturé'); load(); onRefresh(); }
    catch { toast.error('Erreur'); }
  };

  // ── Preuve BAT ────────────────────────────────────────
  const viewBatProof = async () => {
    try { const r = await extranetAPI.getBatProof(id); setBatProof(r.data.data); setProofModal(true); }
    catch (err) { toast.error(err.response?.data?.message || 'Aucune preuve disponible'); }
  };

  const exportProof = () => {
    if (!batProof) return;
    const text = `PREUVE DE SIGNATURE BAT\n${'='.repeat(40)}\nProjet : ${batProof.title}\nClient : ${batProof.client_name} <${batProof.client_email}>\nDate : ${batProof.signed_at}\nAdresse IP : ${batProof.signer_ip}\nHash SHA-256 : ${batProof.proof_hash}\nCommentaire : ${batProof.client_comment || 'Aucun'}\n${'='.repeat(40)}\nGénéré le ${new Date().toLocaleString('fr-FR')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `preuve_bat_${id}.txt`; a.click();
  };

  const copyLink = () => navigator.clipboard.writeText(`${window.location.origin}/extranet/${data?.token}`).then(() => toast.success('Lien copié'));

  // ── Rendu ─────────────────────────────────────────────
  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-lg" /></div>;
  if (!data)   return <div style={{ padding: 24, color: 'var(--error)' }}>Erreur de chargement</div>;

  const files     = data.files || [];
  const selection = data.client_selection_data || (data.client_selection ? JSON.parse(data.client_selection) : null);
  const isClosed  = data.status === 'closed';
  const infoCard  = { background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Infos client ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[['Client', data.client_name], ['Email', data.client_email || '—'], ['Valide jusqu\'au', new Date(data.valid_until).toLocaleDateString('fr-FR')]].map(([l, v]) => (
          <div key={l} style={infoCard}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 3 }}>{l}</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div>
          </div>
        ))}
        <div style={infoCard}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 3 }}>Statut</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Badge status={data.status} />
            {(data.status === 'draft' || data.status === 'sent') && (
              <button onClick={toggleStatus} disabled={toggling}
                style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                {toggling ? '…' : (data.status === 'draft' ? '→ Marquer envoyé' : '→ Remettre brouillon')}
              </button>
            )}
          </div>
        </div>
      </div>

      {data.description && <div style={{ ...infoCard, color: 'var(--text-secondary)', fontSize: 13 }}>{data.description}</div>}

      {/* ── Lien portail ── */}
      <div style={{ padding: 12, background: 'rgba(59,130,246,.04)', border: '1px solid rgba(59,130,246,.15)', borderRadius: 'var(--radius-sm)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', marginBottom: 6 }}>Lien portail client</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <code style={{ flex: 1, fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {window.location.origin}/extranet/{data.token}
          </code>
          <Button size="sm" variant="secondary" onClick={copyLink}>Copier</Button>
          <Button size="sm" variant="secondary" onClick={() => window.open(`/extranet/${data.token}`, '_blank')}>Ouvrir</Button>
        </div>
      </div>

      {/* ── Envoi propositions (Étape 1) ── */}
      {!isClosed && ['draft','sent'].includes(data.status) && (
        <div style={{ padding: 14, background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Étape 1 — Envoyer les propositions au client</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@exemple.fr" style={{ flex: 1 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="form-input form-select" value={tplPropositions} onChange={e => setTplPropositions(e.target.value)} style={{ flex: 1, fontSize: 12, padding: '6px 10px' }}>
              <option value="">Email par défaut (sans template)</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
            </select>
            <Button variant="primary" loading={sending} onClick={sendPropositions}>Envoyer les propositions</Button>
          </div>
        </div>
      )}

      {/* ── Sélection client (visible si le client a sélectionné) ── */}
      {selection && (
        <div style={{ padding: 14, background: 'rgba(124,58,237,.04)', border: '1px solid rgba(124,58,237,.15)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#7c3aed', marginBottom: 8 }}>Sélection du client</div>
          {selection.selected_ids && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(data.products || []).filter(p => selection.selected_ids.includes(p.product_id || p.id)).map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}>
                  {p.main_image && <img src={p.main_image} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
                  <span style={{ fontWeight: 600 }}>{p.product_name || 'Produit'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>x{p.quantity}</span>
                </div>
              ))}
              {selection.selected_ids.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Aucun produit sélectionné</span>}
            </div>
          )}
          {data.selection_at && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Sélectionné le {new Date(data.selection_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
        </div>
      )}

      {/* ── Envoi BAT (Étape 2) ── */}
      {!isClosed && selection && ['active','pending_bat'].includes(data.status) && (
        <div style={{ padding: 14, background: 'rgba(217,119,6,.04)', border: '1px solid rgba(217,119,6,.2)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#d97706', marginBottom: 10 }}>Étape 2 — Envoyer le BAT au client</div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Uploadez les visuels/BAT ci-dessous, puis envoyez au client pour signature.</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="form-input form-select" value={tplBat} onChange={e => setTplBat(e.target.value)} style={{ flex: 1, fontSize: 12, padding: '6px 10px' }}>
              <option value="">Email par défaut (sans template)</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
            </select>
            <Button variant="primary" loading={sendingBat} onClick={sendBat} style={{ background: '#d97706' }}>Envoyer le BAT</Button>
          </div>
        </div>
      )}

      {/* ── Produits proposés ── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Produits proposés ({data.products?.length || 0})</div>
          {!isClosed && (
            <Button size="sm" variant="secondary" onClick={() => setShowAddForm(v => !v)}>
              {showAddForm ? 'Annuler' : '+ Ajouter'}
            </Button>
          )}
        </div>

        {showAddForm && (
          <div style={{ padding: 14, background: 'rgba(59,130,246,.04)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 'var(--radius-sm)', marginBottom: 10 }}>
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <Input value={productSearch}
                onChange={e => { setProductSearch(e.target.value); setAddForm(f => ({ ...f, product_id: null })); }}
                placeholder="Rechercher dans le catalogue (min. 2 lettres)..." />
              {searching && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Recherche...</div>}
              {searchResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-lg)', maxHeight: 200, overflowY: 'auto' }}>
                  {searchResults.map(p => (
                    <div key={p.id} onClick={() => selectProduct(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-page)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {p.main_image && <img src={p.main_image} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />}
                      <div>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.reference} · {p.base_price} €</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
              <FormField label="Prix perso (€)" hint="Vide = catalogue">
                <Input type="number" step="0.01" min="0" value={addForm.custom_price}
                  onChange={e => setAddForm(f => ({ ...f, custom_price: e.target.value }))} placeholder="0.00" />
              </FormField>
              <FormField label="Quantité">
                <Input type="number" min="1" value={addForm.quantity}
                  onChange={e => setAddForm(f => ({ ...f, quantity: e.target.value }))} />
              </FormField>
              <FormField label="Notes">
                <Input value={addForm.notes} onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optionnel" />
              </FormField>
            </div>
            <Button variant="primary" size="sm" loading={addingProduct} onClick={addProduct}>Ajouter</Button>
          </div>
        )}

        {!data.products?.length ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>Aucun produit.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.products.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                {p.main_image && <img src={p.main_image} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)', flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.product_name || 'Produit personnalisé'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Qté : {p.quantity}
                    {p.custom_price ? ` · ${parseFloat(p.custom_price).toFixed(2)} € (perso)` : ' · Prix catalogue'}
                    {p.notes ? ` · ${p.notes}` : ''}
                  </div>
                </div>
                {!isClosed && (
                  <Button size="sm" variant="ghost" loading={removingId === p.id}
                    onClick={() => removeProduct(p.id)} style={{ color: 'var(--error)', flexShrink: 0 }}>Suppr.</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Fichiers & Documents ── */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Fichiers & Documents ({files.length})</div>
          {!isClosed && (
            <label style={{ cursor: uploadingFile ? 'wait' : 'pointer' }}>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip" style={{ display: 'none' }} onChange={uploadFile} disabled={uploadingFile} />
              <span style={{ padding: '5px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 12, fontWeight: 600, background: 'var(--bg-card)' }}>
                {uploadingFile ? 'Upload en cours...' : '+ Ajouter un fichier'}
              </span>
            </label>
          )}
        </div>
        {!files.length ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '10px 0' }}>Aucun fichier. Uploadez vos visuels BAT ou documents ici.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {files.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                <span style={{ fontSize: 20 }}>{f.mime_type?.includes('pdf') ? '📄' : '🖼'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={f.url} target="_blank" rel="noreferrer" style={{ fontWeight: 600, fontSize: 13, color: 'var(--brand-accent)', textDecoration: 'none' }}>{f.original_name}</a>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{(f.file_size / 1024).toFixed(0)} Ko · {new Date(f.uploaded_at).toLocaleDateString('fr-FR')}</div>
                </div>
                {!isClosed && (
                  <Button size="sm" variant="ghost" loading={deletingFileId === f.id}
                    onClick={() => deleteFile(f.id)} style={{ color: 'var(--error)', flexShrink: 0 }}>Suppr.</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── BAT signé ── */}
      {data.bat && (
        <div style={{ padding: '10px 14px', background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--success)' }}>
                BAT signé le {new Date(data.bat.signed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 2, fontFamily: 'monospace' }}>Preuve : {data.bat.proof_hash?.slice(0, 16)}...</div>
            </div>
            <Button size="sm" variant="secondary" onClick={viewBatProof}>Voir la preuve</Button>
          </div>
        </div>
      )}

      {/* ── Actions bas ── */}
      <div style={{ display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {!isClosed && data.status !== 'draft' && (
          <Button variant="ghost" loading={steppingBack} onClick={doStepBack} style={{ color: '#d97706' }}>Étape précédente</Button>
        )}
        {!isClosed && (
          <Button variant="ghost" onClick={close} style={{ color: 'var(--error)', marginLeft: 'auto' }}>Clôturer</Button>
        )}
      </div>

      {/* ── Modal preuve BAT ── */}
      <Modal isOpen={proofModal} onClose={() => setProofModal(false)} title="Preuve de signature BAT" size="md"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={exportProof}>Exporter (.txt)</Button>
          <Button variant="ghost" onClick={() => setProofModal(false)}>Fermer</Button>
        </div>}
      >
        {batProof && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            {[
              ['Projet', batProof.title],
              ['Client', batProof.client_name],
              ['Email', batProof.client_email],
              ['Date de signature', batProof.signed_at],
              ['Adresse IP', batProof.signer_ip],
              ['Commentaire', batProof.client_comment || '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 130, flexShrink: 0, fontWeight: 700, color: 'var(--text-muted)', fontSize: 12 }}>{l}</div>
                <div style={{ flex: 1 }}>{v}</div>
              </div>
            ))}
            <div style={{ marginTop: 8, padding: '10px 14px', background: 'var(--bg-page)', borderRadius: 6, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>HASH SHA-256</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{batProof.proof_hash}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════
//  PAGE PRINCIPALE
// ═════════════════════════════════════════════════════════════
export const AdminExtranetPage = () => {
  const [extranets,  setExtranets]   = useState([]);
  const [loading,    setLoading]     = useState(true);
  const [modal,      setModal]       = useState(false);
  const [detailId,   setDetailId]    = useState(null);
  const [saving,     setSaving]      = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const emptyForm = {
    title: '', client_name: '', client_email: '', client_phone: '',
    description: '', valid_until: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
  };
  const [form, setForm] = useState(emptyForm);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    extranetAPI.list().then(({ data }) => setExtranets(data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title || !form.client_name) { toast.error('Titre et nom client requis'); return; }
    setSaving(true);
    try {
      const res = await extranetAPI.create(form);
      toast.success('Extranet créé');
      setModal(false);
      setDetailId(res.data.data.id);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cet extranet définitivement ?')) return;
    try { await extranetAPI.delete(id); toast.success('Supprimé'); if (detailId === id) setDetailId(null); load(); }
    catch { toast.error('Erreur'); }
  };

  const filtered = statusFilter ? extranets.filter(e => e.status === statusFilter) : extranets;
  const stats = {
    total: extranets.length,
    active: extranets.filter(e => ['sent','active','pending_bat'].includes(e.status)).length,
    signed: extranets.filter(e => e.status === 'bat_signed').length,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Extranets Client</h1>
          <p className="admin-page-subtitle">Propositions produits + signature BAT</p>
        </div>
        <Button variant="primary" onClick={() => { setForm(emptyForm); setModal(true); }}>+ Nouvel extranet</Button>
      </div>

      <div className="admin-page-content">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
          {[['Total', stats.total, '#64748b'], ['En cours', stats.active, '#2563eb'], ['BAT signés', stats.signed, '#16a34a']].map(([l, v, c]) => (
            <div key={l} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 20px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: c, lineHeight: 1.2 }}>{v}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{l}</div>
            </div>
          ))}
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select>
          </div>

          {loading ? <div className="page-loader"><div className="spinner" /></div> :
            !filtered.length ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Aucun extranet</div>
                <Button variant="primary" onClick={() => { setForm(emptyForm); setModal(true); }}>+ Créer un extranet</Button>
              </div>
            ) : (
              <table className="data-table">
                <thead><tr><th>Titre</th><th>Client</th><th>Produits</th><th>Statut</th><th>Valide jusqu'au</th><th>Actions</th></tr></thead>
                <tbody>
                  {filtered.map(e => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 700 }}>{e.title}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{e.client_name}</div>
                        {e.client_email && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.client_email}</div>}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{e.product_count || 0}</td>
                      <td><Badge status={e.status} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(e.valid_until).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button size="sm" variant="secondary" onClick={() => setDetailId(e.id)}>Gérer</Button>
                          <Button size="sm" variant="ghost" onClick={() => del(e.id)}>Suppr.</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {/* Modal création */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title="Nouvel extranet client" size="md"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setModal(false)}>Annuler</Button><Button variant="primary" loading={saving} onClick={save}>Créer</Button></div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <FormField label="Titre du projet" required><Input value={form.title} onChange={e => f('title', e.target.value)} placeholder="Ex: Collection été 2025 — Acme Corp" /></FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Nom du client" required><Input value={form.client_name} onChange={e => f('client_name', e.target.value)} /></FormField>
            <FormField label="Email client"><Input type="email" value={form.client_email} onChange={e => f('client_email', e.target.value)} /></FormField>
            <FormField label="Téléphone"><Input value={form.client_phone} onChange={e => f('client_phone', e.target.value)} /></FormField>
            <FormField label="Valide jusqu'au"><Input type="date" value={form.valid_until} onChange={e => f('valid_until', e.target.value)} /></FormField>
          </div>
          <FormField label="Description / message au client">
            <Textarea rows={3} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Voici notre sélection pour votre projet..." />
          </FormField>
        </div>
      </Modal>

      {/* Modal détail */}
      <Modal isOpen={!!detailId} onClose={() => { setDetailId(null); load(); }} title="Gestion de l'extranet" size="lg">
        {detailId && <ExtranetPanel id={detailId} onRefresh={load} />}
      </Modal>
    </div>
  );
};

export default AdminExtranetPage;
